/**
 * Game Endscreen Component
 * Displays the game over screen with winner announcement and statistics
 */

export interface GameOverData {
  winner: string;
  reason: string;
  rounds_played: number;
  mafia_survivors: string[];
  mafia_eliminated: string[];
  villagers_remaining: string[];
  villagers_eliminated: string[];
}

export class Endscreen {
  private container: HTMLElement | null = null;
  private overlay: HTMLElement | null = null;

  constructor() {
    this.createEndscreenHTML();
  }

  private createEndscreenHTML(): void {
    // Create overlay
    this.overlay = document.createElement("div");
    this.overlay.id = "endscreen-overlay";
    this.overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.85);
      display: none;
      z-index: 1000;
      backdrop-filter: blur(5px);
    `;

    // Create container
    this.container = document.createElement("div");
    this.container.id = "endscreen-container";
    this.container.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 90%;
      max-width: 600px;
      max-height: 90vh;
      background: linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%);
      border: 3px solid #e94560;
      border-radius: 15px;
      padding: 40px;
      z-index: 1001;
      overflow-y: auto;
      display: none;
      box-shadow: 0 10px 50px rgba(233, 69, 96, 0.3);
      font-family: 'Courier New', monospace;
      color: #fff;
    `;

    this.container.innerHTML = `
      <div id="endscreen-content" style="text-align: center;">
        <h1 id="endscreen-title" style="
          font-size: 3em;
          margin: 0 0 10px 0;
          text-transform: uppercase;
          text-shadow: 0 0 20px rgba(233, 69, 96, 0.8);
        ">GAME OVER</h1>
        
        <div id="endscreen-winner" style="
          font-size: 2.5em;
          margin: 20px 0;
          font-weight: bold;
          color: #ffd700;
          text-shadow: 0 0 15px rgba(255, 215, 0, 0.6);
        "></div>
        
        <div id="endscreen-reason" style="
          font-size: 1.1em;
          margin: 15px 0 30px 0;
          color: #aaa;
          font-style: italic;
        "></div>
        
        <div id="endscreen-stats" style="
          margin: 30px 0;
          text-align: left;
          background: rgba(0, 0, 0, 0.3);
          padding: 20px;
          border-radius: 10px;
          border-left: 4px solid #e94560;
        ">
          <div style="font-size: 1.3em; margin-bottom: 15px; color: #ffd700;">📊 GAME STATISTICS</div>
          
          <div style="margin: 10px 0;">
            <strong>Total Rounds:</strong> <span id="stat-rounds">0</span>
          </div>
          
          <div style="margin: 15px 0; padding-top: 15px; border-top: 1px solid rgba(233, 69, 96, 0.3);">
            <div style="color: #e94560; font-weight: bold; margin-bottom: 8px;">🔴 MAFIA</div>
            <div style="margin: 5px 0 5px 20px;">
              <strong>Survivors:</strong> <span id="stat-mafia-survivors" style="color: #ff6b6b;">None</span>
            </div>
            <div style="margin: 5px 0 5px 20px;">
              <strong>Eliminated:</strong> <span id="stat-mafia-eliminated" style="color: #aaa;">None</span>
            </div>
          </div>
          
          <div style="margin: 15px 0; padding-top: 15px; border-top: 1px solid rgba(233, 69, 96, 0.3);">
            <div style="color: #4ecdc4; font-weight: bold; margin-bottom: 8px;">🟢 VILLAGERS</div>
            <div style="margin: 5px 0 5px 20px;">
              <strong>Survivors:</strong> <span id="stat-villagers-remaining" style="color: #4ecdc4;">None</span>
            </div>
            <div style="margin: 5px 0 5px 20px;">
              <strong>Eliminated:</strong> <span id="stat-villagers-eliminated" style="color: #aaa;">None</span>
            </div>
          </div>
        </div>
        
        <button id="endscreen-restart-btn" style="
          margin-top: 30px;
          padding: 12px 30px;
          font-size: 1.1em;
          background: linear-gradient(135deg, #e94560 0%, #ff6b9d 100%);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          text-transform: uppercase;
          font-weight: bold;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(233, 69, 96, 0.4);
        ">Start New Game</button>
      </div>
    `;

    document.body.appendChild(this.overlay);
    document.body.appendChild(this.container);
  }

  show(gameOverData: GameOverData, onRestart: () => void): void {
    if (!this.container || !this.overlay) return;

    // Update content
    const title = document.getElementById("endscreen-title");
    const winnerDiv = document.getElementById("endscreen-winner");
    const reasonDiv = document.getElementById("endscreen-reason");

    if (title && winnerDiv && reasonDiv) {
      // Determine winner display
      if (gameOverData.winner === "Mafia") {
        title.style.color = "#e94560";
        winnerDiv.innerHTML = "🔴 MAFIA WINS! 🔴";
        winnerDiv.style.color = "#ff6b6b";
      } else {
        title.style.color = "#4ecdc4";
        winnerDiv.innerHTML = "🟢 VILLAGERS WIN! 🟢";
        winnerDiv.style.color = "#4ecdc4";
      }

      reasonDiv.innerHTML = gameOverData.reason;
    }

    // Update stats
    document.getElementById("stat-rounds")!.innerHTML = String(
      gameOverData.rounds_played
    );
    document.getElementById("stat-mafia-survivors")!.innerHTML =
      gameOverData.mafia_survivors.length > 0
        ? gameOverData.mafia_survivors.join(", ")
        : "None";
    document.getElementById("stat-mafia-eliminated")!.innerHTML =
      gameOverData.mafia_eliminated.length > 0
        ? gameOverData.mafia_eliminated.join(", ")
        : "None";
    document.getElementById("stat-villagers-remaining")!.innerHTML =
      gameOverData.villagers_remaining.length > 0
        ? gameOverData.villagers_remaining.join(", ")
        : "None";
    document.getElementById("stat-villagers-eliminated")!.innerHTML =
      gameOverData.villagers_eliminated.length > 0
        ? gameOverData.villagers_eliminated.join(", ")
        : "None";

    // Setup button
    const restartBtn = document.getElementById("endscreen-restart-btn") as HTMLButtonElement;
    if (restartBtn) {
      restartBtn.onclick = () => {
        this.hide();
        onRestart();
      };
    }

    // Show endscreen
    this.overlay.style.display = "block";
    this.container.style.display = "block";

    // Add animation
    if (this.container) {
      this.container.style.animation = "endscreen-pop-in 0.5s ease-out";
    }
  }

  hide(): void {
    if (this.overlay) this.overlay.style.display = "none";
    if (this.container) this.container.style.display = "none";
  }

  isVisible(): boolean {
    return this.overlay?.style.display !== "none";
  }
}

// Add animation styles to document
if (!document.getElementById("endscreen-animations")) {
  const style = document.createElement("style");
  style.id = "endscreen-animations";
  style.innerHTML = `
    @keyframes endscreen-pop-in {
      0% {
        transform: translate(-50%, -50%) scale(0.8);
        opacity: 0;
      }
      100% {
        transform: translate(-50%, -50%) scale(1);
        opacity: 1;
      }
    }

    #endscreen-container::-webkit-scrollbar {
      width: 8px;
    }

    #endscreen-container::-webkit-scrollbar-track {
      background: rgba(233, 69, 96, 0.1);
      border-radius: 10px;
    }

    #endscreen-container::-webkit-scrollbar-thumb {
      background: rgba(233, 69, 96, 0.5);
      border-radius: 10px;
    }

    #endscreen-container::-webkit-scrollbar-thumb:hover {
      background: rgba(233, 69, 96, 0.7);
    }
  `;
  document.head.appendChild(style);
}
